import { type Security, safeAbsurd } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useSecuritySchemaContextSelectors } from 'components/SecuritySchema/model/SecuritySchemaModel';
import {
  isListingScope,
  isReferencedTestScope,
  isRuleChainScope,
} from 'components/SecuritySchema/model/SecuritySchemaScope';
import { selectActiveReferencedTest } from 'components/SecuritySchema/model/selectors/selectActiveReferencedTest';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC } from 'react';
import { navigateToBreadcrumb } from './model/commands/navigateToBreadcrumb';

import classNames from 'classnames';
import { openSecuritySchemaScope } from 'components/SecuritySchema/model/commands/openSecuritySchemaScope';

export const SecuritySchemaBreadcrumbs: FC = () => {
  const ctxProps = useSecuritySchemaContextSelectors({
    breadcrumbs: prop('breadcrumbs'),
    dispatch: prop('dispatch'),
    hostname: prop('hostname'),
    scope: prop('scope'),
    activeReferencedTest: selectActiveReferencedTest,
  });
  const msgBreadcrumbs = useLocale().securitySchema.breadcrumbs;
  const makeOpenEntityList =
    (entityType?: Security.SecurityType | 'referencedTest') => () => {
      ctxProps.dispatch(openSecuritySchemaScope, {
        scope: {
          type: 'scopeList',
          entityType,
        },
      });
    };

  /**
   * Determined from first rule chain in the breadcrumbs
   *
   * This is a bit hack because originally breadcrumbs navigation was designed
   * to contain only rule chains from a single security machine (same security type).
   * Later it turned out it is useful for cross-type links (PBR's EvalTest referring to ACL rules),
   *
   * It makes best sense that security type top-level breadcrumb refer to type of first breadcrumb
   * rather than currently active one. There is no state field telling what is "current root type
   * security machine" for current view, so we determine it from first saved breadcrumb if available.
   */
  const currentSecurityType = (() => {
    if (ctxProps.breadcrumbs.length > 0) {
      return ctxProps.breadcrumbs[0]?.securityType;
    }
    if (isRuleChainScope(ctxProps.scope)) {
      return ctxProps.scope.securityType;
    }
    return null;
  })();

  const hostnameClickable = isListingScope(ctxProps.scope);

  const renderHostnameBreadcrumb = () =>
    ctxProps.hostname && (
      <div
        className={classNames(
          'breadcrumbs__breadcrumb breadcrumbs__breadcrumb--primary',
          !hostnameClickable && 'breadcrumbs__breadcrumb--clickable',
        )}
        onClick={
          hostnameClickable
            ? undefined
            : makeOpenEntityList(
                isRuleChainScope(ctxProps.scope)
                  ? ctxProps.scope.securityType
                  : undefined,
              )
        }
      >
        {ctxProps.hostname}
      </div>
    );

  const renderScopeTypeBreadcrumb = () => {
    if (ctxProps.scope === null) {
      return null;
    }
    if (isRuleChainScope(ctxProps.scope)) {
      return (
        <div
          className='breadcrumbs__breadcrumb breadcrumbs__breadcrumb--default breadcrumbs__breadcrumb--clickable'
          onClick={makeOpenEntityList(
            currentSecurityType ?? ctxProps.scope.securityType,
          )}
        >
          {
            msgBreadcrumbs.scopeType[
              currentSecurityType ?? ctxProps.scope.securityType
            ]
          }
        </div>
      );
    }
    if (isReferencedTestScope(ctxProps.scope)) {
      return (
        <div
          className='breadcrumbs__breadcrumb breadcrumbs__breadcrumb--default breadcrumbs__breadcrumb--clickable'
          onClick={makeOpenEntityList('referencedTest')}
        >
          {msgBreadcrumbs.scopeType.referencedTest}
        </div>
      );
    }
    if (isListingScope(ctxProps.scope)) {
      return ctxProps.scope.entityType ? (
        <div className='breadcrumbs__breadcrumb breadcrumbs__breadcrumb--default'>
          {msgBreadcrumbs.scopeType[ctxProps.scope.entityType]}
        </div>
      ) : null;
    }
    safeAbsurd(ctxProps.scope);
    return null;
  };

  const getPrefixForDifferentScopeBreadcrumb = (
    breadcrumbSecurityType: Security.SecurityType,
  ) =>
    breadcrumbSecurityType !== currentSecurityType
      ? `${msgBreadcrumbs.scopeTypeShort[breadcrumbSecurityType]}: `
      : null;

  const renderBreadcrumbsFromState = () =>
    ctxProps.breadcrumbs.map((breadcrumb, index) => (
      <div
        className='breadcrumbs__breadcrumb breadcrumbs__breadcrumb--default breadcrumbs__breadcrumb--clickable'
        // biome-ignore lint/suspicious/noArrayIndexKey: no better key there
        key={index}
        onClick={() => {
          ctxProps.dispatch(navigateToBreadcrumb, { index });
        }}
      >
        {getPrefixForDifferentScopeBreadcrumb(breadcrumb.securityType)}
        {breadcrumb.ruleChainName}
      </div>
    ));

  const renderCurrentEntityBreadcrumb = () => {
    if (ctxProps.scope === null) {
      return null;
    }
    if (isRuleChainScope(ctxProps.scope)) {
      return (
        <div className='breadcrumbs__breadcrumb breadcrumbs__breadcrumb--default'>
          {getPrefixForDifferentScopeBreadcrumb(ctxProps.scope.securityType)}
          {ctxProps.scope.name}
        </div>
      );
    }
    if (isReferencedTestScope(ctxProps.scope)) {
      return (
        <div className='breadcrumbs__breadcrumb breadcrumbs__breadcrumb--default'>
          {ctxProps.activeReferencedTest?.name ?? ctxProps.scope.name}
        </div>
      );
    }
    if (isListingScope(ctxProps.scope)) {
      return null;
    }
    safeAbsurd(ctxProps.scope);
    return null;
  };

  return (
    <div className='breadcrumbs'>
      {renderHostnameBreadcrumb()}
      {renderScopeTypeBreadcrumb()}
      {renderBreadcrumbsFromState()}
      {renderCurrentEntityBreadcrumb()}
    </div>
  );
};
